import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useLang } from "../../context/LangContext";
import { useV2Session } from "../context/V2SessionContext";
import { getV2Copy } from "../content";
import { validateWeightKg } from "../lib/v2NutritionUtils";

export default function V2Register() {
  const { lang } = useLang();
  const { startRegistration } = useV2Session();
  const copy = getV2Copy(lang);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkEmail, setCheckEmail] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    weight_kg: "",
  });

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading) return;
    setError("");

    const weightValidation = validateWeightKg(form.weight_kg);
    if (!weightValidation.valid) {
      setError(weightValidation.error);
      return;
    }

    setLoading(true);
    const result = await startRegistration({
      full_name: form.full_name,
      email: form.email,
      weight_kg: weightValidation.weightKg,
    });
    setLoading(false);

    if (!result.ok) {
      setError(result.error || "Registration could not be started.");
      return;
    }

    setCheckEmail(true);
  };

  return (
    <main className="min-h-screen bg-background px-4 py-12 text-foreground">
      <section className="mx-auto max-w-xl rounded-lg border border-border bg-card p-6">
        <h1 className="mt-3 text-3xl font-extrabold">{copy.register}</h1>
        <p className="mt-3 text-muted-foreground">{copy.authNotes}</p>

        {checkEmail ? (
          <div className="mt-6 rounded-lg border border-border bg-background p-4">
            <h2 className="text-xl font-bold">Check your email to continue registration</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Your account is not complete yet. Open the verification link, then set your password.
            </p>
            <div className="mt-4">
              <Link to="/login">
                <Button type="button" variant="outline">
                  {copy.login}
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <Label htmlFor="v2-register-name">{copy.fullName}</Label>
              <Input
                id="v2-register-name"
                autoComplete="name"
                value={form.full_name}
                onChange={(event) => updateField("full_name", event.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="v2-register-email">{copy.email}</Label>
              <Input
                id="v2-register-email"
                type="email"
                autoComplete="email"
                value={form.email}
                onChange={(event) => updateField("email", event.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="v2-register-weight">{copy.weight}</Label>
              <Input
                id="v2-register-weight"
                type="number"
                min="0"
                step="0.1"
                value={form.weight_kg}
                onChange={(event) => updateField("weight_kg", event.target.value)}
                required
              />
            </div>
            {error && <p className="text-sm font-medium text-destructive">{error}</p>}

            <div className="flex flex-wrap gap-3">
              <Button type="submit" disabled={loading}>
                {loading ? "Creating registration..." : "Create account"}
              </Button>
              <Link to="/guest">
                <Button type="button" variant="outline" disabled={loading}>
                  {copy.guest}
                </Button>
              </Link>
              <Link to="/login">
                <Button type="button" variant="ghost" disabled={loading}>
                  {copy.login}
                </Button>
              </Link>
            </div>
          </form>
        )}
      </section>
    </main>
  );
}
